'use client';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useRef, useState, useEffect } from 'react';

mapboxgl.accessToken = 'pk.eyJ1IjoiY2lsZXRvIiwiYSI6ImNseHM2M2N0YjEzZXAya29oanYwN3FqemsifQ.u3oFdtYA0z3-qg6PqjQqCA';

type MapProps = {
  sectionName: string;
};

export default function Map({ sectionName }: MapProps) {
  const mapContainer = useRef<any>(null);
  const map = useRef<any>(null);
  const [zoom, setZoom] = useState(12);
  const mapStyle = {
    borderRadius: '10px',
    height: '580px',
    width: '1000px'
  };

  const locations: { [key: string]: [number, number] } = {
    'San José': [-84.087502, 9.934739],
    'Curridabat': [-84.0387, 9.9146],
    'Heredia': [-84.1198, 9.9981],
    'Escazú': [-84.1462, 9.9207]
  };

  const [selectedButton, setSelectedButton] = useState<keyof typeof locations | null>('San José');
  const [currentMarker, setCurrentMarker] = useState<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-84.07678611658973, 9.955602047581564],
      zoom: zoom,
      attributionControl: false
    });

    map.current.scrollZoom.disable();
    map.current.addControl(new mapboxgl.NavigationControl());

    const navigationControlContainer = document.querySelector('.mapboxgl-ctrl-top-right') as HTMLElement;
      if (navigationControlContainer) {
          navigationControlContainer.style.top = '13px';
      }

    map.current.on('load', () => {
      setMapLoaded(true);
      
      const markerElement = document.createElement('div');
        markerElement.className = 'custom-marker';
        markerElement.style.backgroundImage = sectionName === 'eventos' ? 'url(/images/decoration/mapPinPink.svg)' : 'url(/images/decoration/mapPin.svg)';      markerElement.style.backgroundRepeat = 'no-repeat';
        markerElement.style.width = '50px';
        markerElement.style.height = '60px';
        markerElement.style.backgroundSize = '100%';
        markerElement.style.marginTop = '8px';

      const popupContent = document.createElement('div');
        popupContent.style.display = 'flex';
        popupContent.style.flexDirection = 'row';
        popupContent.style.alignItems = 'center';
        popupContent.style.backgroundColor = '#0000000F';
        popupContent.style.borderRadius = '10px';
        popupContent.style.padding = '10px';

      const buildingImage = document.createElement('div');
        buildingImage.className = 'building-image-small';
        buildingImage.style.display = 'flex';
        buildingImage.style.width = '160px';
        buildingImage.style.height = '120px';

      const contentContainer = document.createElement('div');
        contentContainer.style.padding = '10px';

      const title = document.createElement('span');
        title.style.fontWeight = 'bold';
        title.style.color = '#F68729';
        title.style.fontSize = '16px';
        title.textContent = 'Amura Centro Culinario';

      const icon = document.createElement('i');
        icon.className = 'faLocationDot';
      
      const iconContainer = document.createElement('span');
        iconContainer.style.fontSize = '16px';
        iconContainer.style.marginRight = '8px';
        iconContainer.style.color = 'orange';
        iconContainer.appendChild(icon);

      const descriptionText = document.createTextNode('400 metros al este del cruce de Llorente de Tibás.');

      const description = document.createElement('p');
        description.style.textAlign = 'center';
        description.style.maxWidth = '300px';
        description.style.fontSize = '14px';
        description.style.marginTop = '8px';
        description.appendChild(iconContainer);
        description.appendChild(descriptionText);
        contentContainer.appendChild(title);
        contentContainer.appendChild(description);

        popupContent.appendChild(buildingImage);
        popupContent.appendChild(contentContainer);

      const popup = new mapboxgl.Popup({ offset: 25 })
        .setMaxWidth('400px')
        .setDOMContent(popupContent);

      const marker = new mapboxgl.Marker(markerElement) 
        .setLngLat([-84.0761111111111, 9.956341111111110])
        .setPopup(popup)
        .addTo(map.current);
        calculateAndDisplayRoute(locations['San José']);
    });
  }, [zoom]);

  const calculateAndDisplayRoute = (end: [number, number]) => {
    if (!map.current || !mapLoaded) {
      return;
    }

    const start = [-84.0761111111111, 9.956211111111110];
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson&access_token=${mapboxgl.accessToken}`;
  
    const decorationColor = sectionName === 'eventos' ? 'red' : 'orange';

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (!map.current || !mapLoaded) {
          return;
        }

        try {
          const route = data.routes[0].geometry;
          const coordinates = route.coordinates;
          const endCoordinate = coordinates[coordinates.length - 1];

          if (map.current.getSource('route')) {
            map.current.getSource('route').setData({
              type: 'Feature',
              properties: {},
              geometry: route
            });
          } else {
            map.current.addSource('route', {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: route
              }
            });
    
            map.current.addLayer({
              id: 'route',
              type: 'line',
              source: 'route',
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': decorationColor,
                'line-width': 3,
                'line-dasharray': [2, 2]
              }
            });
          }

          const endMarkerDot = document.createElement('div');
            endMarkerDot.style.width = '10px';
            endMarkerDot.style.height = '10px';
            endMarkerDot.style.backgroundColor = decorationColor;
            endMarkerDot.style.borderRadius = '50%';
            endMarkerDot.style.transform = 'translate(-50%, -50%)';
            endMarkerDot.style.zIndex = 'z-0';

            if (currentMarker) {
              currentMarker.remove();
            }

          const newMarker = new mapboxgl.Marker(endMarkerDot)
            .setLngLat(endCoordinate)
            .addTo(map.current);
            newMarker.getElement().style.zIndex = '1000';
          
            setCurrentMarker(newMarker);
        } catch (error) {
          console.error('Error adding route to map:', error);
        }
      })
      
      .catch(error => console.error('Error fetching route:', error));
      
  };

  return (
    <div className="relative">
      <div className="max-w-[800px] flex justify-around items-center gap-7 text-sm text-[#505D68] bg-gradient-to-br from-white via-transparent to-white bg-opacity-55 backdrop-blur-lg rounded-3xl shadow-xl border border-white mr-4 ml-4 px-2.5 py-2.5 absolute top-5 left-24 -right-0 z-10">
        <button
          className={`flex ${selectedButton === 'San José' ? sectionName === 'eventos' ? 'bg-red text-white px-5 py-3 hover:bg-red' : 'bg-[#FDDB50] text-[#5D2916] px-5 py-3 hover:bg-[#FDDB50]' : 'hover:bg-gray-200'} rounded-full px-5 py-3`}
            onClick={() => {
            setSelectedButton('San José');
            calculateAndDisplayRoute(locations['San José']);
          }}
        >
          <span className="font-bold mr-1">San José:</span>
          <span className="font-normal">5 min</span>
        </button>
        <button
          className={`flex ${selectedButton === 'Curridabat' ? sectionName === 'eventos' ? 'bg-red text-white px-5 py-3 hover:bg-red' : 'bg-[#FDDB50] text-[#5D2916] px-5 py-3 hover:bg-[#FDDB50]' : 'hover:bg-gray-200'} rounded-full px-5 py-3`}
          onClick={() => {
            setSelectedButton('Curridabat');
            calculateAndDisplayRoute(locations['Curridabat']);
          }}
        >
          <span className="font-bold mr-1">Curridabat:</span>
          <span className="font-normal">20 min</span>
        </button>
        <button
          className={`flex ${selectedButton === 'Heredia' ? sectionName === 'eventos' ? 'bg-red text-white px-5 py-3 hover:bg-red' : 'bg-[#FDDB50] text-[#5D2916] px-5 py-3 hover:bg-[#FDDB50]' : 'hover:bg-gray-200'} rounded-full px-5 py-3`}
          onClick={() => {
            setSelectedButton('Heredia');
            calculateAndDisplayRoute(locations['Heredia']);
          }}
        >
          <span className="font-bold mr-1">Heredia:</span>
          <span className="font-normal">20 - 25 min</span>
        </button>
        <button
          className={`flex ${selectedButton === 'Escazú' ? sectionName === 'eventos' ? 'bg-red text-white px-5 py-3 hover:bg-red' : 'bg-[#FDDB50] text-[#5D2916] px-5 py-3 hover:bg-[#FDDB50]' : 'hover:bg-gray-200'} rounded-full px-5 py-3`}
          onClick={() => {
            setSelectedButton('Escazú');
            calculateAndDisplayRoute(locations['Escazú']);
          }}
        >
          <span className="font-bold mr-1">Escazú:</span>
          <span className="font-normal">15 - 20 min</span>
        </button>
      </div>
      <div ref={mapContainer} style={mapStyle}></div>
    </div>
  );
};